import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import logo from '../../pacePackPrime.png';
import { useStaffNames, STAFF_CONFIG_PATH } from '../../hooks/useStaffNames';
import {
  Box,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

const UserPackageSummary = () => {
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const { staffNames, loading: staffLoading, error: staffError } = useStaffNames();
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('packageCount');
  const [sortOrder, setSortOrder] = useState('desc');
  const [authUser] = useAuthState(auth);
  const navigate = useNavigate();

  // Fetch packages
  useEffect(() => {
    const fetchPackages = async () => {
      if (!authUser) return;
      try {
        const snapshot = await getDocs(collection(db, 'packages'));
        const pkgList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPackages(pkgList);
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        setPackagesLoading(false);
      }
    };
    fetchPackages();
  }, [authUser]);

  const loading = packagesLoading || staffLoading;

  // Apply date range filter to packages
  const dateFilteredPackages = packages.filter(pkg => {
    if (!pkg.createdAt?.seconds) return !startDate && !endDate; // keep undated pkgs only if no filter applied
    const pkgDate = new Date(pkg.createdAt.seconds * 1000);

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      if (pkgDate < start) return false;
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (pkgDate > end) return false;
    }
    return true;
  });

  // Count packages per staff member (within date range). Staff share a central
  // login and tag themselves on Add Package, so createdBy holds their name.
  const packageCountByStaff = dateFilteredPackages.reduce((acc, pkg) => {
    if (!pkg.createdBy) return acc;
    acc[pkg.createdBy] = (acc[pkg.createdBy] || 0) + 1;
    return acc;
  }, {});

  // Packages created before the switch to names still carry a uid in createdBy,
  // so they belong to nobody in the list - surface the total rather than hide it.
  const unattributedCount = dateFilteredPackages.filter(
    pkg => !pkg.createdBy || !staffNames.includes(pkg.createdBy)
  ).length;

  // Build rows: one per staff member
  const rows = staffNames.map(name => ({
    name,
    packageCount: packageCountByStaff[name] || 0,
  }));

  // Search filter (name)
  const searchTerm = search.toLowerCase();
  const filteredRows = rows.filter(row => {
    if (!searchTerm) return true;
    return row.name.toLowerCase().includes(searchTerm);
  });

  // Sort
  const sortedRows = [...filteredRows].sort((a, b) => {
    let aVal, bVal;
    switch (sortBy) {
      case 'name':
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case 'packageCount':
      default:
        aVal = a.packageCount;
        bVal = b.packageCount;
        break;
    }
    if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleRowClick = (staffName) => {
    // Pass the active date filters along so the detail page opens with the same range applied
    const params = new URLSearchParams();
    if (startDate) params.set('start', startDate);
    if (endDate) params.set('end', endDate);
    const qs = params.toString();
    navigate(`/userpackagedetail/${encodeURIComponent(staffName)}${qs ? `?${qs}` : ''}`);
  };

  const clearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1280, xl: 1600 }, p: { xs: 2, md: 3 }, px: { lg: 4 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mb: { xs: 2, md: 3 } }}
        >
          <Box component="img" src={logo} alt="Logo" sx={{ height: { xs: 64, md: 96 }, width: 'auto', objectFit: 'contain' }} />
          <Typography variant="h5" fontWeight={600} color="text.primary">
            Package Creation by Staff Member
          </Typography>
        </Stack>

        {/* Filters */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
            <TextField
              label="Search staff member"
              placeholder="Name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label="From date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="To date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>

        {/* Table */}
        {loading ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={22} sx={{ mr: 1.5, verticalAlign: 'middle' }} />
            <Typography component="span" color="text.secondary">Loading staff members...</Typography>
          </Paper>
        ) : staffError ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="error">
              Could not load the staff list from <Box component="span" sx={{ fontFamily: 'monospace' }}>{STAFF_CONFIG_PATH}</Box>. Try refreshing the page.
            </Typography>
          </Paper>
        ) : staffNames.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No staff names are set up yet. Add a <Box component="span" sx={{ fontFamily: 'monospace' }}>names</Box> array to the{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{STAFF_CONFIG_PATH}</Box> document in Firestore.
            </Typography>
          </Paper>
        ) : sortedRows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No staff members match your filters.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'desc'}
                      onClick={() => handleSort('name')}
                    >
                      Staff Member
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'packageCount'}
                      direction={sortBy === 'packageCount' ? sortOrder : 'desc'}
                      onClick={() => handleSort('packageCount')}
                    >
                      Packages Created
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow
                    key={row.name}
                    hover
                    onClick={() => handleRowClick(row.name)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {row.packageCount}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
          Showing {sortedRows.length} of {rows.length} staff members
          {(startDate || endDate) && ' (filtered by date range)'}
        </Typography>
        {unattributedCount > 0 && (
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 0.5 }}>
            {unattributedCount} package{unattributedCount === 1 ? '' : 's'} in this range {unattributedCount === 1 ? 'is' : 'are'} not
            tagged to a staff member on the list (created before staff tagging, or by a name since removed).
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default UserPackageSummary;